import type {
  DeliveryMode,
  Order,
  OrderDraft,
  OrderDraftGroup,
  OrderDraftLine,
  OrderLine,
  OrderModifier,
  OrderStatus,
  PaymentMethod,
} from '../models/order/order.model';
import type { Paged } from '../models/paged.model';
import { readProp, toId, toNumber, toText } from '../../shared/utils/parse';

/** Языконезависимый знак умножения для «Сыр ×2» — не строка словаря. */
const QTY_PREFIX = '×';
type ServerOrderType = 'DELIVERY' | 'PICK_UP';

type ServerPaymentType = 'CASH' | 'CLICK' | 'PAYME';

type ServerOrderSource = 'WEBSITE' | 'BOT';

interface OrderAddressPayload {
  address: string;
  latitude: number;
  longitude: number;
}

interface OrderModificationPayload {
  id: number;
  quantity: number;
}

interface OrderModificationGroupPayload {
  id: number;
  modifications: OrderModificationPayload[];
}

interface OrderItemPayload {
  positionId: number;
  quantity: number;
  comment: string | null;
  modificationGroups?: OrderModificationGroupPayload[];
}

/** Тело `POST /order`. Цен и итога здесь нет и быть не может: суммы считает сервер. */
export interface OrderPayload {
  /** Филиал заказа: при самовывозе обязателен, при доставке — для сверки остатка по нему же. */
  branchId?: number;
  type: ServerOrderType;
  address?: OrderAddressPayload;
  comment: string | null;
  deliveryComment: string | null;
  paymentType: ServerPaymentType;
  items: OrderItemPayload[];
  /** Канал заказа сервер сам не ставит — берёт из запроса, поэтому шлём всегда. */
  source: ServerOrderSource;
}

/** Потолок поля `address` у бэкенда (гайд § 5.1): строка заказа собирается из трёх полей формы. */
const ADDRESS_LINE_MAX = 500;

// Map, а не объект: ключ приходит с сервера, у объекта нашлись бы `toString` и прототип.
const SERVER_ORDER_STATUS = new Map<string, OrderStatus>([
  ['CREATED', 'new'],
  ['IN_PROGRESS', 'cooking'],
  ['ON_WAY', 'delivering'],
  // COMPLETED — кухня закончила, но заказ ещё не отдан: это «Готов», а не «Доставлен».
  ['COMPLETED', 'ready'],
  ['PAID', 'done'],
  ['PAID_DUE', 'done'],
  ['CLOSED', 'done'],
  ['CANCELLED', 'cancelled'],
]);

/** Новый статус на бэкенде не должен ронять заказ — показываем его как только что созданный. */
const DEFAULT_ORDER_STATUS: OrderStatus = 'new';

// `DINE_IN` сайт не оформляет, но получить его в истории может: зал ближе к самовывозу, чем к доставке.
const SERVER_DELIVERY_MODE = new Map<string, DeliveryMode>([
  ['DELIVERY', 'delivery'],
  ['PICK_UP', 'pickup'],
]);

const DEFAULT_DELIVERY_MODE: DeliveryMode = 'pickup';

// После оплаты сервер переписывает тип на провайдера, которым реально заплатили.
const SERVER_PAYMENT_METHOD = new Map<string, PaymentMethod>([
  ['CASH', 'cash'],
  ['CLICK', 'click'],
  ['PAYME', 'payme'],
]);

const DEFAULT_PAYMENT_METHOD: PaymentMethod = 'cash';

/** Бонусами платить нечем — бонусов на бэкенде нет, поэтому `bonus` уходит наличными. */
const SERVER_PAYMENT_TYPE: Record<PaymentMethod, ServerPaymentType> = {
  cash: 'CASH',
  click: 'CLICK',
  payme: 'PAYME',
  bonus: 'CASH',
};

/** Незнакомый статус → null: кнопку «Оплатить» без уверенности не показываем (fail-safe). */
const SERVER_PAYMENT_STATUS = new Map<string, boolean>([
  ['PAID', true],
  ['NOT_PAID', false],
  ['UNPAID', false],
  ['PENDING', false],
  ['WAITING', false],
  ['CREATED', false],
]);

/** Spring `Page` → срез докрутки. `last` по умолчанию true: битая метаданная останавливает пагинацию, а не зацикливает. */
export function mapOrderPage(payload: unknown): Paged<Order> {
  const content = readArray(readProp(payload, 'content')) ?? [];

  const orders: Order[] = [];
  for (const item of content) {
    const order = mapOrder(item);
    // Заказ без id или итога не показать — выбрасываем его целиком, а не «чиним».
    if (order !== null) {
      orders.push(order);
    }
  }

  return {
    items: orders,
    last: readProp(payload, 'last') !== false,
  };
}

export function mapOrder(payload: unknown): Order | null {
  const rawId = readProp(payload, 'id');
  const id = toId(rawId);
  // Свежему заказу с сайта касса ещё не присвоила номер (orderNumber: null) — до тех пор «№» = серверный id.
  const number = toNumber(readProp(payload, 'orderNumber')) ?? toNumber(rawId);
  const total = toNumber(readProp(payload, 'totalPrice'));
  if (id === null || number === null || total === null) {
    return null;
  }

  const mode = readMode(readProp(payload, 'type'));
  // В списке `items` приходит null — состав есть только в деталке заказа.
  const items = readArray(readProp(payload, 'items'));
  const lines = items === null ? null : mapLines(items);
  const createdAtIso = readIso(readProp(payload, 'createdDate'));

  return {
    id,
    number,
    createdAtIso: createdAtIso ?? '',
    // Времена этапов бэкенд обещал, но ещё не отдаёт (11.09.2026) — до тех пор null, таймлайн без часов.
    stageAtIso: {
      new: createdAtIso,
      cooking: readIso(readProp(payload, 'inProgressTime')),
      ready: readIso(readProp(payload, 'cookedTime')),
      delivering: readIso(readProp(payload, 'onWayTime')),
      done: readIso(readProp(payload, 'deliveredTime')),
    },
    status: readStatus(readProp(payload, 'status')),
    mode,
    address: toText(readProp(readProp(payload, 'address'), 'address')),
    paymentMethod: readPaymentMethod(readProp(readProp(payload, 'payment'), 'type')),
    paid: readPaymentStatus(readProp(readProp(payload, 'payment'), 'status')),
    comment: toText(readProp(payload, 'comment')),
    lines,
    itemCount: readInt(readProp(payload, 'itemCount'), 0) ?? lines?.length ?? 0,
    // Итог как есть: price строк — без добавок и до скидки, складывать строки нельзя.
    total,
    totalDiscount: toNumber(readProp(payload, 'totalDiscount')),
    deliveryPrice: toNumber(readProp(payload, 'deliveryPrice')),
    branchName: toText(readProp(payload, 'branchName')),
  };
}

/** null — черновик невалиден: такой заказ отвергнет и сервер, звонить ему незачем. */
export function buildOrderPayload(draft: OrderDraft): OrderPayload | null {
  const items = buildItems(draft.lines);
  if (items === null) {
    return null;
  }

  // Филиал шлём в обоих режимах: сервер сверит остаток по тому же филиалу, что и бейджи каталога.
  const branchId = toServerId(draft.branchId);
  if (draft.mode === 'pickup' && branchId === null) {
    return null;
  }

  const address = draft.mode === 'delivery' ? buildAddress(draft) : null;
  if (draft.mode === 'delivery' && address === null) {
    return null;
  }

  const payload: OrderPayload = {
    type: draft.mode === 'delivery' ? 'DELIVERY' : 'PICK_UP',
    // Пустая строка = поля нет: в payload пустым строкам места нет.
    comment: toText(draft.comment),
    // Комментарий курьеру на заказе без курьера — мусор в кассе мерчанта.
    deliveryComment: draft.mode === 'delivery' ? toText(draft.deliveryComment) : null,
    paymentType: SERVER_PAYMENT_TYPE[draft.paymentMethod],
    items,
    source: draft.fromTelegram ? 'BOT' : 'WEBSITE',
  };

  const withBranch = branchId === null ? payload : { ...payload, branchId };
  // Адрес — только для доставки и только целиком: строка без координат сервером отвергается.
  return address === null ? withBranch : { ...withBranch, address };
}

function buildAddress(draft: OrderDraft): OrderAddressPayload | null {
  const address = toText(draft.address);
  const point = draft.point;
  if (address === null || point === null) {
    return null;
  }
  if (!Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) {
    return null;
  }
  // Строка собрана из трёх полей формы и может перерасти серверные 500 символов — режем здесь.
  return {
    address: address.slice(0, ADDRESS_LINE_MAX),
    latitude: point.latitude,
    longitude: point.longitude,
  };
}

/** null вместо пропуска строки: молча увезти позицию из заказа хуже, чем отказать в оформлении. */
function buildItems(lines: readonly OrderDraftLine[]): OrderItemPayload[] | null {
  if (lines.length === 0) {
    return null;
  }

  const items: OrderItemPayload[] = [];
  for (const line of lines) {
    // У варианта собственный id позиции в каталоге — он и уходит вместо id товара.
    const positionId = toServerId(line.variantId ?? line.productId);
    const quantity = toCount(line.qty);
    if (positionId === null || quantity === null) {
      return null;
    }

    const item: OrderItemPayload = { positionId, quantity, comment: toText(line.comment) };
    const modificationGroups = buildGroups(line.groups);
    items.push(modificationGroups.length === 0 ? item : { ...item, modificationGroups });
  }
  return items;
}

/** Ключ — id группы: одна и та же группа в черновике дважды уедет одной записью, как ждёт сервер. */
function buildGroups(groups: readonly OrderDraftGroup[]): OrderModificationGroupPayload[] {
  const byGroup = new Map<number, OrderModificationPayload[]>();

  for (const group of groups) {
    const id = toServerId(group.groupId);
    if (id === null) {
      continue;
    }

    const modifications = byGroup.get(id) ?? [];
    for (const item of group.items) {
      const itemId = toServerId(item.id);
      const quantity = toCount(item.qty);
      if (itemId !== null && quantity !== null) {
        modifications.push({ id: itemId, quantity });
      }
    }

    // Группа без выбранных дополнений серверу не нужна — она же пустая.
    if (modifications.length > 0) {
      byGroup.set(id, modifications);
    }
  }

  return [...byGroup].map(([id, modifications]) => ({ id, modifications }));
}

function mapLines(items: readonly unknown[]): OrderLine[] {
  const lines: OrderLine[] = [];
  for (const item of items) {
    const line = mapLine(item);
    if (line !== null) {
      lines.push(line);
    }
  }
  return lines;
}

function mapLine(item: unknown): OrderLine | null {
  const name = toText(readProp(item, 'name'));
  const qty = readInt(readProp(item, 'quantity'), 1);
  const unitPrice = toNumber(readProp(item, 'price'));
  if (name === null || qty === null || unitPrice === null) {
    return null;
  }

  return {
    // `productId` здесь только ключ в списке состава, поэтому пустой id строку не рушит.
    productId: toId(readProp(item, 'positionId')) ?? '',
    name,
    modifiers: readModifiers(readProp(item, 'modificationGroups')),
    qty,
    unitPrice,
    discountPercent: readDiscountPercent(item),
  };
}

/** Иной тип скидки или мусор рисуем без скидки: итоги всё равно серверные. */
function readDiscountPercent(item: unknown): number | null {
  if (toText(readProp(item, 'discountType')) !== 'PERCENTAGE') {
    return null;
  }
  const percent = toNumber(readProp(item, 'discount'));
  return percent !== null && percent > 0 && percent <= 100 ? percent : null;
}

function readModifiers(groups: unknown): OrderModifier[] {
  const list = readArray(groups);
  if (list === null) {
    return [];
  }

  const modifiers: OrderModifier[] = [];
  for (const group of list) {
    const items = readArray(readProp(group, 'modifications'));
    if (items === null) {
      continue;
    }
    for (const modification of items) {
      const name = toText(readProp(modification, 'name'));
      if (name === null) {
        continue;
      }
      const qty = readInt(readProp(modification, 'quantity'), 1) ?? 1;
      modifiers.push({
        // Количество подписываем только когда оно есть: «Сыр ×1» — шум.
        label: qty > 1 ? `${name} ${QTY_PREFIX}${qty}` : name,
        // Мусорная или отрицательная цена → 0: имя без цены честнее выброшенной добавки.
        amount: Math.max(0, toNumber(readProp(modification, 'price')) ?? 0) * qty,
      });
    }
  }
  return modifiers;
}

function readStatus(value: unknown): OrderStatus {
  const key = toText(value);
  return (key === null ? undefined : SERVER_ORDER_STATUS.get(key)) ?? DEFAULT_ORDER_STATUS;
}

function readMode(value: unknown): DeliveryMode {
  const key = toText(value);
  return (key === null ? undefined : SERVER_DELIVERY_MODE.get(key)) ?? DEFAULT_DELIVERY_MODE;
}

function readPaymentMethod(value: unknown): PaymentMethod {
  const key = toText(value);
  return (key === null ? undefined : SERVER_PAYMENT_METHOD.get(key)) ?? DEFAULT_PAYMENT_METHOD;
}

// toUpperCase — страховка: точное серверное значение статуса ещё не подтверждено (план §10.1).
function readPaymentStatus(value: unknown): boolean | null {
  const key = toText(value);
  return (key === null ? undefined : SERVER_PAYMENT_STATUS.get(key.toUpperCase())) ?? null;
}

/** Обратный ход: серверные id — целые положительные, дробь и минус означают мусор в черновике. */
function toServerId(value: string | null): number | null {
  const text = toText(value);
  if (text === null) {
    return null;
  }
  const id = Number(text);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function toCount(value: number): number | null {
  return Number.isInteger(value) && value > 0 ? value : null;
}

function readInt(value: unknown, min: number): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= min ? value : null;
}

/** Нечитаемая дата с бэкенда обрушила бы DatePipe при рендере — считаем её отсутствующей. */
function readIso(value: unknown): string | null {
  const text = toText(value);
  return text !== null && !Number.isNaN(Date.parse(text)) ? text : null;
}

/** Единственное место с приведением: `Array.isArray` сужает `unknown` до `any[]`. */
function readArray(value: unknown): readonly unknown[] | null {
  return Array.isArray(value) ? (value as readonly unknown[]) : null;
}
